/**
 * Utility functions for handling content thumbnails
 * Provides placeholder images for content types when no thumbnail is available
 */

import assetCacheService from './assetCacheService';

/**
 * Get placeholder thumbnail URL based on content type
 * Uses SVG data URI instead of external service for reliability
 * @param {string} contentType - The type of content (video, podcast, simulation, tool, course, book)
 * @param {number} width - Image width (default: 400)
 * @param {number} height - Image height (default: 300)
 * @returns {string} Placeholder image URL (SVG data URI)
 */
export const getPlaceholderThumbnail = (contentType, width = 400, height = 300) => {
  const contentTypeMap = {
    video: 'Video',
    podcast: 'Podcast',
    simulation: 'Simulation',
    tool: 'Tool',
    course: 'Course',
    book: 'Book',
    news: 'News'
  };
  
  const label = contentTypeMap[contentType?.toLowerCase()] || 'Content';
  
  // Color scheme based on content type
  const colorMap = {
    video: { bg: '4F46E5', text: 'FFFFFF' }, // Indigo
    podcast: { bg: '7C3AED', text: 'FFFFFF' }, // Purple
    simulation: { bg: '0891B2', text: 'FFFFFF' }, // Cyan
    tool: { bg: 'F59E0B', text: 'FFFFFF' }, // Amber
    course: { bg: 'EC4899', text: 'FFFFFF' }, // Pink
    book: { bg: '10B981', text: 'FFFFFF' }, // Emerald
    news: { bg: '6366F1', text: 'FFFFFF' }, // Indigo
  };
  
  const colors = colorMap[contentType?.toLowerCase()] || { bg: '6B7280', text: 'FFFFFF' };
  
  // Create SVG placeholder as data URI
  const fontSize = Math.min(width, height) / 8;
  const svg = `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg"><rect width="100%" height="100%" fill="#${colors.bg}"/><text x="50%" y="50%" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="bold" fill="#${colors.text}" text-anchor="middle" dominant-baseline="middle">${label}</text></svg>`;
  
  // Encode SVG for data URI (URL encode to handle special characters)
  const encodedSvg = encodeURIComponent(svg);
  return `data:image/svg+xml,${encodedSvg}`;
};

/**
 * Get thumbnail URL for content item
 * Falls back to placeholder if no thumbnail_url is provided
 * @param {Object} item - Content item with thumbnail_url and content_type
 * @param {number} width - Placeholder width (default: 400)
 * @param {number} height - Placeholder height (default: 300)
 * @returns {string} Thumbnail URL (either from API or placeholder)
 */
export const getContentThumbnail = (item, width = 400, height = 300) => {
  // If thumbnail_url is provided, use it
  if (item?.thumbnail_url) {
    return item.thumbnail_url;
  }
  
  // Special case for video: try YouTube thumbnail if video_id exists
  if (item?.content_type === 'video' && item?.youtube_video_id) {
    const youtubeThumbnailUrl = `https://img.youtube.com/vi/${item.youtube_video_id}/maxresdefault.jpg`;
    
    // Cache the YouTube thumbnail in the background
    assetCacheService.cacheThumbnail(youtubeThumbnailUrl).catch(error => {
      console.warn('Failed to cache YouTube thumbnail:', error);
    });
    
    return youtubeThumbnailUrl;
  }
  
  // Fall back to placeholder based on content type
  return getPlaceholderThumbnail(item?.content_type || item?.type, width, height);
};

/**
 * Get thumbnail URL for news item
 * Falls back to news placeholder if no thumbnail_url is provided
 * @param {Object} news - News item with thumbnail_url
 * @param {number} width - Placeholder width (default: 200)
 * @param {number} height - Placeholder height (default: 200)
 * @returns {string} Thumbnail URL (either from API or placeholder)
 */
export const getNewsThumbnail = (news, width = 200, height = 200) => {
  if (news?.thumbnail_url) {
    // Cache external thumbnails
    assetCacheService.cacheThumbnail(news.thumbnail_url).catch(error => {
      console.warn('Failed to cache news thumbnail:', error);
    });
    
    return news.thumbnail_url;
  }
  return getPlaceholderThumbnail('news', width, height);
};

